import * as Yup from 'yup';
import { PHONE_DIGITS_PATTERN } from './contactValidationPatterns';

// The TLD is not length-capped: `.agency`, `.online` and `.marketing` are ordinary business domains, and
// the API validates against the full IANA list, so a cap here only rejects addresses the server accepts.
export const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const customFieldNameRegex = /^[a-zA-Z0-9_-]+$/;

const phonenumberRegex = PHONE_DIGITS_PATTERN;

export const hexRegex = /^#([A-Fa-f0-9]{3,4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
export const rgbRgbaRegex =
  /^rgba?\(\s*(\d{1,3}%?)\s*,\s*(\d{1,3}%?)\s*,\s*(\d{1,3}%?)\s*(?:,\s*([01]?\.\d+|\d{1,3}%?)\s*)?\)$/i;

export const loginValidationSchema = Yup.object().shape({
  email: Yup.string().matches(emailRegex, 'Invalid email format.').required('Email is required.'),
  password: Yup.string().required('Password is required.'),
});

export const emailValidationSchema = Yup.object().shape({
  email: Yup.string().matches(emailRegex, 'Invalid email format.').required('Email is required.'),
});

export const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters long.')
    .required('Password is required.'),
});

export type ValidationErrors = {
  [key: string]: string;
};

/** Only meaningful for a `validate` call made with `abortEarly: false`; otherwise `inner` is empty. */
export const collectValidationErrors = (error: Yup.ValidationError): ValidationErrors => {
  return error.inner.reduce((acc, curr) => {
    if (curr.path) acc[curr.path] = curr.message;
    return acc;
  }, {} as ValidationErrors);
};

export const groupValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
});

export const keywordValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
});

export const customFieldValidationDescriptionSchema = Yup.object().shape({
  description: Yup.string().required('Description is required.').max(32),
  type: Yup.string().required('Type is required'),
});

export const customFieldValidationNameSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required.')
    .matches(
      customFieldNameRegex,
      'Invalid name format. Only upper/lowercase letters (A-z), digits (0-9), underscore (_), dash (-) are allowed',
    ),
});

export const createContactValidationSchema = Yup.object().shape(
  {
    phoneNumber: Yup.string()
      .required('Phone Number is required.')
      .matches(phonenumberRegex, 'Invalid phone number.'),
    email: Yup.string()
      .notRequired()
      .when('email', {
        is: (value: string) => value?.length,
        then: rule => rule.matches(emailRegex, 'Invalid email format.'),
      }),
  },
  [['email', 'email']],
);

export const messageCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  groupIds: Yup.array().min(1, 'Groups are required.'),
  //
  scheduledMessages: Yup.array().of(
    Yup.object().shape({
      scheduledType: Yup.string().required(),
      time: Yup.string()
        .nullable()
        .when('scheduledType', {
          is: 'time',
          then: schema => schema.required('Time is required'),
        }),
    }),
  ),
});

export const messageValidationSchema = Yup.object().shape({
  text: Yup.string().required('Message is required.'),
});

export const joinCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  groupIds: Yup.array().min(1, 'Groups are required.'),
});

export const textToJoinCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  keywordIds: Yup.array().min(1, 'Keywords are required.'),
  groupIds: Yup.array().min(1, 'Groups are required.'),
});

export const appointmentReminderCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  appointmentTypeId: Yup.string().required('Calendar is required.'),
});

export const birthdayCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
});

export const annualCampaignValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  metadataName: Yup.string().required('Contact Date Time is required.'),
});

export const updateUserValidationSchema = Yup.object().shape(
  {
    phoneNumber: Yup.string()
      .notRequired()
      .when('phoneNumber', {
        is: (value: string) => value?.length,
        then: rule => rule.matches(phonenumberRegex, 'Invalid phone number.'),
      }),
    firstName: Yup.string().required('First Name is required.'),
    lastName: Yup.string().required('Last Name is required.'),
  },
  [['phoneNumber', 'phoneNumber']],
);

export const inviteUserValidationSchema = Yup.object().shape(
  {
    phoneNumber: Yup.string()
      .notRequired()
      .when('phoneNumber', {
        is: (value: string) => value?.length,
        then: rule => rule.matches(phonenumberRegex, 'Invalid phone number.'),
      }),
    email: Yup.string().matches(emailRegex, 'Invalid email format.').required('Email is required.'),
    firstName: Yup.string().required('First Name is required.'),
    lastName: Yup.string().required('Last Name is required.'),
    roleId: Yup.string().required('Role is required.'),
  },
  [['phoneNumber', 'phoneNumber']],
);

export const addIntegrationValidationSchema = Yup.object().shape(
  {
    name: Yup.string().required('Name is required.'),
    url: Yup.string().required('Url required.').url('Invalid url format.'),
  },
  [['name', 'url']],
);

export const addApikeyValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  permissions: Yup.array().min(1, 'Permissions are required.'),
});

export const addAdminCompanyValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  application: Yup.object({
    id: Yup.string().required('Application is required.'),
  }),
  createMessageProfile: Yup.boolean(),
});

export const editAdminCompanyValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  application: Yup.object({
    id: Yup.string().required('Application is required.'),
  }),
});

export const editCompanyValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  applicationId: Yup.string().required('Application is required.'),
  timezone: Yup.string().required('Timezone is required.'),
  billingEmail: Yup.string().nullable().matches(emailRegex, 'Invalid email format.'),
});

export const addMessageTemplateValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  text: Yup.string().required('Template text is required.'),
});

export const addAwayCampaignValidationSchema = Yup.object().shape({
  text: Yup.string().required('Text is required.'),
});

export const addAppointmentTypeValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  color: Yup.string().required('Color text is required.'),
});

export const addAppointmentValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  appointmentType: Yup.object().required('Calendar is required.'),
  startDate: Yup.string().required('Start date is required.'),
  endDate: Yup.string().required('End date is required.'),
});

export const campaignConfigCategoryValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required.'),
  title: Yup.string().required('Title is required.'),
  description: Yup.string().nullable(),
  icon: Yup.string().required('Icon is required.'),
  rank: Yup.number().min(1, 'Rank must be at least 1').required('Rank is required.'),
});

export const shortUrlValidationSchema = Yup.object().shape(
  {
    url: Yup.string().required('Please enter a URL'),
    hasExpiration: Yup.boolean(),
    expirationDate: Yup.mixed()
      .nullable()
      .when('hasExpiration', {
        is: true,
        then: schema => schema
          .required('Please select an expiration date')
          .test(
            'is-valid-date',
            'Please select a valid date and time',
            function(value: unknown) {
              if (!value) return false;
              
              if (value && typeof value === 'object') {
                if ('isValid' in value && typeof (value as { isValid: () => boolean }).isValid === 'function') {
                  return (value as { isValid: () => boolean }).isValid();
                }
                if (value instanceof Date) {
                  return !isNaN(value.getTime());
                }
              }
              
              const dateValue = new Date(value as string | number | Date);
              return !isNaN(dateValue.getTime());
            }
          ),
        otherwise: schema => schema.nullable(),
      }),
  },
  [['hasExpiration', 'expirationDate']],
);
